import type { Hotspot } from './imageTypes';

/**
 * Locate the red annotation arrows in a screenshot, in the browser.
 *
 * A faithful port of tools/find_arrows.py. It exists because the admin level
 * editor runs in a browser and the host has no Pillow — but the reason it is a
 * PORT rather than a fresh guess is the project rule that arrow regions are
 * measured from the red pixels and never hand-estimated. Two implementations
 * that disagree would quietly break that guarantee, so the thresholds, the
 * neighbour radius, the minimum area and the padding are all copied exactly.
 *
 * If you change one, change the other and re-measure an existing level to
 * confirm the numbers still match what is committed.
 */

export interface DetectedBlob {
  /** Measured bounding box in pixels, before padding. */
  pixels: { x: number; y: number; w: number; h: number };
  /** The region to store on the arrow: percentages, padded for touch. */
  region: Hotspot;
  /** How many red pixels made up this blob. Small blobs are usually UI icons. */
  area: number;
}

export interface DetectionResult {
  width: number;
  height: number;
  redPixels: number;
  blobs: DetectedBlob[];
  /**
   * Areas of blobs that fell under the threshold, largest first.
   *
   * Surfaced rather than swallowed: a blob just under the cut is far more
   * likely to be a real thin arrow than noise, and on the levels added by hand
   * two genuine arrows were lost exactly this way.
   */
  dropped: number[];
}

export interface DetectOptions {
  minArea?: number;
  gap?: number;
}

/** Saturated red only. Matches is_red() in find_arrows.py. */
function isRed(r: number, g: number, b: number): boolean {
  return r > 120 && r - g > 55 && r - b > 55;
}

/**
 * Read an image file's pixels.
 *
 * createImageBitmap handles every format the browser can display, which is the
 * same set the game can serve, so there is no separate decoder to keep in step.
 */
export async function readPixels(
  file: Blob,
): Promise<{ data: Uint8ClampedArray; width: number; height: number }> {
  const bitmap = await createImageBitmap(file);
  // Read the dimensions BEFORE close(). Closing releases the bitmap and resets
  // width and height to 0, so returning bitmap.width afterwards reports a 0x0
  // image and the detector finds nothing at all.
  const width = bitmap.width;
  const height = bitmap.height;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) throw new Error('could not read the image');
  context.drawImage(bitmap, 0, 0);
  const image = context.getImageData(0, 0, width, height);
  bitmap.close();
  return { data: image.data, width, height };
}

export function detectArrows(
  data: Uint8ClampedArray,
  width: number,
  height: number,
  options: DetectOptions = {},
): DetectionResult {
  const minArea = options.minArea ?? 250;
  const gap = options.gap ?? 8;

  // Flat boolean mask; indexed y * width + x. A Set of "x,y" strings is what
  // the Python does, but at 2000x871 that is 1.7M strings and the tab stalls.
  const red = new Uint8Array(width * height);
  let redPixels = 0;
  for (let i = 0, p = 0; i < data.length; i += 4, p += 1) {
    if (isRed(data[i] ?? 0, data[i + 1] ?? 0, data[i + 2] ?? 0)) {
      red[p] = 1;
      redPixels += 1;
    }
  }

  const seen = new Uint8Array(width * height);
  const blobs: DetectedBlob[] = [];
  const dropped: number[] = [];
  // Reused across blobs; holds pixel indices, so one allocation for the run.
  const queue = new Int32Array(width * height);

  for (let start = 0; start < red.length; start += 1) {
    if (!red[start] || seen[start]) continue;

    let head = 0;
    let tail = 0;
    queue[tail++] = start;
    seen[start] = 1;

    let x0 = start % width;
    let x1 = x0;
    let y0 = (start / width) | 0;
    let y1 = y0;
    let area = 0;

    while (head < tail) {
      const point = queue[head++] ?? 0;
      const px = point % width;
      const py = (point / width) | 0;
      area += 1;
      if (px < x0) x0 = px;
      if (px > x1) x1 = px;
      if (py < y0) y0 = py;
      if (py > y1) y1 = py;

      // The neighbour radius bridges the break between an arrowhead and its
      // tail where the stroke thins.
      const loY = Math.max(0, py - gap);
      const hiY = Math.min(height - 1, py + gap);
      const loX = Math.max(0, px - gap);
      const hiX = Math.min(width - 1, px + gap);
      for (let ny = loY; ny <= hiY; ny += 1) {
        const row = ny * width;
        for (let nx = loX; nx <= hiX; nx += 1) {
          const next = row + nx;
          if (red[next] && !seen[next]) {
            seen[next] = 1;
            queue[tail++] = next;
          }
        }
      }
    }

    if (area < minArea) {
      dropped.push(area);
      continue;
    }

    const w = x1 - x0;
    const h = y1 - y0;

    // Pad thin arrows so they stay a comfortable touch target. Same arithmetic
    // as the Python, including the clamp back inside the image.
    const pw = (w / width) * 100;
    const ph = (h / height) * 100;
    const tw = Math.max(pw, 3.0);
    const th = Math.max(ph, 5.0);
    let px = (x0 / width) * 100 - (tw - pw) / 2;
    let py = (y0 / height) * 100 - (th - ph) / 2;
    px = Math.max(0, Math.min(100 - tw, px));
    py = Math.max(0, Math.min(100 - th, py));

    blobs.push({
      pixels: { x: x0, y: y0, w, h },
      region: { x: round(px), y: round(py), w: round(tw), h: round(th) },
      area,
    });
  }

  // Reading order: banded by row so a wide screenshot numbers left-to-right
  // across each band rather than snaking. Matches the Python's sort key.
  blobs.sort((a, b) => {
    const bandA = Math.floor(a.pixels.y / 120);
    const bandB = Math.floor(b.pixels.y / 120);
    return bandA === bandB ? a.pixels.x - b.pixels.x : bandA - bandB;
  });
  dropped.sort((a, b) => b - a);

  return { width, height, redPixels, blobs, dropped };
}

function round(value: number): number {
  return Math.round(value * 100) / 100;
}
